import { useState, useRef } from 'react';
import { Check, Sparkles, TrendingUp, Heart, Activity } from "lucide-react";
import { useApp } from '@/context/app-context';
import { streamAIResponse, buildAISummary } from '@/lib/ai-service';
import { getAIConfig, getAIUsage, incrementAIUsage } from '@/lib/ai-config';
import { Logo } from '@/components/logo';

export function AICoach() {
  const { state, insights } = useApp();
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usage, setUsage] = useState(getAIUsage());
  const abortControllerRef = useRef<AbortController | null>(null);

  const config = getAIConfig();
  const canUseAI = config.provider === 'free' || !!config.apiKey;
  const remaining = 5 - usage.count;
  const remainingDaily = Math.max(0, remaining);

  const features = [
    {
      icon: TrendingUp,
      title: "Recovery Optimization",
      description: "Get personalized rest day recommendations based on your training volume.",
      color: "#FF6B35",
      bgColor: "#3A2318",
    },
    {
      icon: Heart,
      title: "Form & Technique Analysis",
      description: "Get personalized rest day recommendations based on your training volume.",
      color: "#FF6B35",
      bgColor: "#3A2318",
    },
    {
      icon: Activity,
      title: "Muscle Balance Analysis",
      description: "Identifies muscle group imbalances and suggests corrective exercises.",
      color: "#FFA033",
      bgColor: "#3A2318",
    },
  ];

  // Training data summary
  const totalWorkouts = new Set((state.history || []).map((h) => h.date)).size;
  const totalSessions = (state.history || []).length;
  const totalWeightEntries = (state.weightLog || []).length;
  const totalSets = (state.history || []).reduce((sum, h) =>
    sum + (h.exercises || []).reduce((eSum, e) => eSum + (e.sets || []).length, 0), 0);

  const handleGetInsights = async () => {
    if (remainingDaily <= 0) {
      setError('Daily limit reached. Try again tomorrow.');
      return;
    }

    if (!canUseAI) {
      setError('Please configure your AI API key in settings.');
      return;
    }

    setLoading(true);
    setResponse('');
    setError('');
    abortControllerRef.current = new AbortController();

    try {
      const summary = buildAISummary(state, insights);
      let fullResponse = '';

      streamAIResponse(
        config.provider || 'free',
        config.apiKey || '',
        summary,
        (chunk: string) => {
          fullResponse = chunk;
          setResponse(fullResponse);
        },
        () => {
          setLoading(false);
          const newUsage = incrementAIUsage();
          setUsage(newUsage);
        },
        (err: any) => {
          if (err.name !== 'AbortError') {
            setError(err instanceof Error ? err.message : err.message || 'Failed to get AI response');
          }
          setLoading(false);
        },
        abortControllerRef.current.signal,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get AI response');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
  };

  return (
    <div className="p-5 pb-24" style={{ backgroundColor: '#18181B' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5" style={{ height: '32px' }}>
        <Logo />
        <div
          className="px-2.5 py-1 rounded-full flex items-center gap-1"
          style={{ backgroundColor: config.apiKey ? '#27272A' : '#3F3F46', color: '#FFA033', fontSize: '11px', fontWeight: 600 }}
        >
          <span>{config.provider === 'free' ? 'Free' : config.provider === 'openai' ? 'OpenAI' : 'Gemini'}</span>
          {config.apiKey && <Check size={10} />}
        </div>
      </div>

      {/* Title */}
      <h2 style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
        AI Coach
      </h2>

      <p style={{ color: '#71717A', fontSize: '14px', marginBottom: '24px', lineHeight: '1.6' }}>
        With your AI provider you can get personalized and AI-generated coaching tailored insights.
      </p>

      {/* Feature Cards */}
      <div className="space-y-3 mb-6">
        {features.map((feature, index) => {
          const FeatureIcon = feature.icon;
          return (
            <div
              key={index}
              className="p-5"
              style={{
                backgroundColor: '#27272A',
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.06)'
              }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="flex items-center justify-center rounded-lg flex-shrink-0"
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: feature.bgColor,
                  }}
                >
                  <FeatureIcon size={24} style={{ color: feature.color }} />
                </div>
                <div className="flex-1">
                  <h3 style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700, marginBottom: '6px' }}>
                    {feature.title}
                  </h3>
                  <p style={{ color: '#71717A', fontSize: '13px', lineHeight: '1.5' }}>
                    {feature.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Data Summary */}
      <div
        className="p-4 rounded-xl mb-5"
        style={{ backgroundColor: '#27272A' }}
      >
        <span style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px' }}>
          TRAINING DATA SUMMARY
        </span>
        <div className="flex items-center justify-between">
          <div className="text-center">
            <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
              {totalWorkouts}
            </div>
            <div style={{ color: '#71717A', fontSize: '11px' }}>
              workouts
            </div>
          </div>
          <div className="text-center">
            <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
              {totalSessions}
            </div>
            <div style={{ color: '#71717A', fontSize: '11px' }}>
              sessions
            </div>
          </div>
          <div className="text-center">
            <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
              {totalWeightEntries}
            </div>
            <div style={{ color: '#71717A', fontSize: '11px' }}>
              weight entries
            </div>
          </div>
          <div className="text-center">
            <div style={{ color: '#F0F0F0', fontSize: '16px', fontWeight: 700 }}>
              {totalSets}
            </div>
            <div style={{ color: '#71717A', fontSize: '11px' }}>
              total sets
            </div>
          </div>
        </div>
      </div>

      {/* Get Insights Button */}
      {!loading && (
        <button
          onClick={handleGetInsights}
          disabled={!canUseAI || remainingDaily <= 0}
          className="w-full rounded-xl flex items-center justify-center gap-2 mb-4"
          style={{
            backgroundColor: (!canUseAI || remainingDaily <= 0) ? '#71717A' : '#FF6B35',
            color: '#18181B',
            padding: '16px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: (!canUseAI || remainingDaily <= 0) ? 'not-allowed' : 'pointer',
            opacity: (!canUseAI || remainingDaily <= 0) ? 0.5 : 1,
          }}
        >
          <Sparkles size={20} />
          <span>Get AI Insights</span>
        </button>
      )}

      {loading && (
        <button
          onClick={handleCancel}
          className="w-full rounded-xl flex items-center justify-center gap-2 mb-4"
          style={{
            backgroundColor: '#EF4444',
            color: '#F0F0F0',
            padding: '16px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          <span>Cancel</span>
        </button>
      )}

      {/* Usage Info */}
      <div
        className="p-4 rounded-xl"
        style={{ backgroundColor: '#27272A' }}
      >
        <p style={{ color: '#71717A', fontSize: '12px', textAlign: 'center', lineHeight: '1.5' }}>
          {config.provider === 'free' ? (
            <>Generate personalized recommendations via auto-generated AI-based on this week's data. Limited to 3 uses per week.</>
          ) : (
            <>{remainingDaily} analyses remaining today.</>
          )}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="p-4 rounded-xl mt-4"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
        >
          <p style={{ color: '#FCA5A5', fontSize: '13px', lineHeight: '1.5' }}>
            {error}
          </p>
        </div>
      )}

      {/* AI Response */}
      {response && (
        <div
          className="p-5 rounded-xl mt-4"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <p style={{ color: '#71717A', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '12px', textTransform: 'uppercase' }}>
            AI Coaching Insights
          </p>
          <div style={{ color: '#F0F0F0', fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
            {response}
          </div>
        </div>
      )}

      {!response && !loading && !error && (
        <div
          className="p-5 rounded-xl mt-4 text-center"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <p style={{ color: '#71717A', fontSize: '13px', lineHeight: '1.5' }}>
            {!canUseAI
              ? 'Configure your AI API key in settings to get started'
              : 'Click "Get AI Insights" to receive personalized coaching insights'}
          </p>
        </div>
      )}

      {/* Additional Info */}
      <div className="mt-6 space-y-4">
        <div
          className="p-5 rounded-xl"
          style={{ backgroundColor: '#27272A' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: '#A855F7' }}
            ></div>
            <span style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600 }}>
              How It Works
            </span>
          </div>
          <p style={{ color: '#71717A', fontSize: '13px', lineHeight: '1.6' }}>
            Our AI analyzes your workout history, progression patterns, and recovery data to provide science-backed recommendations. Each insight is tailored to your specific training style and goals.
          </p>
        </div>

        <div
          className="p-5 rounded-xl"
          style={{ backgroundColor: '#27272A' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: '#FFA033' }}
            ></div>
            <span style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600 }}>
              Privacy & Data
            </span>
          </div>
          <p style={{ color: '#71717A', fontSize: '13px', lineHeight: '1.6' }}>
            Your training data is processed securely and never shared with third parties. AI insights are generated in real-time and stored only on your device.
          </p>
        </div>
      </div>
    </div>
  );
}
